/**
 * A4 · কোড যাচাই — `/auth/code`
 * Frame: `Oprappo P0 Screens.dc.html`, "A4 · কোড যাচাই".
 *
 * Frame note: "The voice fallback is a visible block, not a link that appears after a
 * failure. SMS delivery here is unreliable enough that it earns the space."
 *
 * CORRECTION (defect 1, Bangla digits in Manrope): the filled OTP cell is drawn
 * `600 26px/1 Manrope,sans-serif` carrying a Bangla digit. Manrope has no Bangla digits.
 * Set in Anek Bangla at 26px; 26px is at or above DISPLAY_BAND_PX so the leading is 1.25,
 * not 1.75 (docs/02 § line height). Listed in the WP-C report.
 *
 * `POST /v1/auth/otp/verify` is sent the moment the fourth cell is filled: the frame draws
 * no submit control, so completing the code IS the submission. Focusing a cell selects
 * what is in it, a digit moves to the next cell, backspace on an empty one moves back,
 * and a pasted code spreads across the four.
 *
 * ── W2-12 · WHAT CHANGED, RECORDED IN `docs/deviations/W2-12.md` ─────────────
 *
 *   · Reached with no number handed over — a bookmark, a cold open — there is nothing to
 *     verify, and the screen sends the person to A3 instead of drawing a number that is
 *     not theirs. The frame's own sample number is gone from this file.
 *   · The cells start EMPTY, and the first is focused. The frame draws a code already
 *     half-typed, which a real person would read as a digit they did not type.
 *   · "আবার পাঠানো যাবে ০:৪২ পরে" counts down for real, to the code's `expires_at`, and
 *     at zero becomes a button that asks for a new code (`countdown.ts` says why the
 *     code's own expiry is the clock).
 *   · A wrong code answers under the cells with the Worker's sentence and the tries left,
 *     instead of replacing the whole screen with the যাচাই অপেক্ষমাণ frame.
 *   · The voice block is a Telegram block. There is no voice channel anywhere in
 *     `apps/api/src/auth`, and no code travels by SMS; the block keeps its drawn shape —
 *     a sand panel, a heading, a sentence and a 46px teal-ruled button — with authored
 *     words that are true (`copy.ts § code`).
 */
import { useRef, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { IconButton } from '@oprappo/ui';
import { Admitted, InlineAnswer, answerFor, isInline, noteFailure, renderAuthFailure, type AuthAnswer } from '../../auth/failure.js';
import { toAsciiDigits, toBanglaDigits, verifyOtp, requestOtp, DEFAULT_DIALLING_CODE, type OtpChannel } from '../../auth/otp.js';
import { ONBOARDING_COPY, resendInBn } from './copy.js';
import { clockBn, useCountdown } from './countdown.js';
import {
  TelegramPanel,
  beginTelegram,
  stepAfterResend,
  type CodeIntent,
  type CodeScreenState,
  type TelegramStep,
} from './door.js';
import { Frame, SAND_FRAME } from './frame.js';
import { takeTurnstileToken, useTurnstile } from './turnstile.js';

export type { CodeIntent, CodeScreenState } from './door.js';

const CELLS = [0, 1, 2, 3] as const;
const EMPTY: readonly string[] = ['', '', '', ''];

const WHERE_VERIFY = 'A4 · POST /v1/auth/otp/verify';
const WHERE_RESEND = 'A4 · POST /v1/auth/otp/request';

/** Where A4 sends a person who arrived with nothing to verify. */
export const NO_HANDOVER_PATH = '/auth/phone';

/**
 * What A3 and A7 hand over, when this screen was reached from one of them; `null` when
 * it was not — a reload that lost the state, a bookmark, a shared link.
 */
export function handoverFrom(state: unknown): CodeScreenState | null {
  if (typeof state !== 'object' || state === null) return null;
  const s = state as Record<string, unknown>;
  const phone = s['phone_e164'];
  if (typeof phone !== 'string' || !/^\+[1-9]\d{6,14}$/.test(phone)) return null;
  const channel = s['channel'];
  const next = s['next'];
  return {
    phone_e164: phone,
    intent: s['intent'] === 'login' ? 'login' : 'signup',
    ...(typeof s['expires_at'] === 'string' ? { expires_at: s['expires_at'] } : {}),
    ...(channel === 'whatsapp' || channel === 'telegram' ? { channel } : {}),
    // Re-checked here: `location.state` is the page's own, but it outlives the page.
    ...(typeof next === 'string' && next.startsWith('/') && !next.startsWith('//') && !next.startsWith('/\\')
      ? { next }
      : {}),
  };
}

/**
 * `+8801XXXXXXXXX` → the national number in Bangla digits, grouped 5·3·3 as A4 draws a
 * number. Numerals only: the sentence around it is the drawn one, with this value in the
 * place the design already put a number.
 */
export function nationalBangla(e164: string): string {
  const digits = e164.startsWith(DEFAULT_DIALLING_CODE)
    ? `0${e164.slice(DEFAULT_DIALLING_CODE.length)}`
    : e164.replace('+', '');
  const grouped =
    digits.length === 11
      ? `${digits.slice(0, 5)} ${digits.slice(5, 8)} ${digits.slice(8)}`
      : digits;
  return toBanglaDigits(grouped);
}

/** Where a verified code lands: A5 for a sign-up, `next` or home for a return. */
export function landingFor(handover: CodeScreenState): string {
  return handover.intent === 'login' ? (handover.next ?? '/') : '/onboarding/profile';
}

export function CodeScreen(): JSX.Element {
  const location = useLocation();
  const handover = handoverFrom(location.state);
  if (handover === null) return <Navigate to={NO_HANDOVER_PATH} replace />;
  return <CodeEntry handover={handover} />;
}

/** What A4 says under its cells: an answer to a failure, or that a new code went out. */
type CodeNotice = { kind: 'answer'; answer: AuthAnswer } | { kind: 'resent' } | { kind: 'telegram_unavailable' };

function CodeEntry({ handover }: { handover: CodeScreenState }): JSX.Element {
  const navigate = useNavigate();
  const [digits, setDigits] = useState<readonly string[]>(EMPTY);
  const [expiresAt, setExpiresAt] = useState<string | null>(handover.expires_at ?? null);
  /** The channel a resend asks for: the one A3/A7 asked for, or Telegram once chosen here. */
  const [channel, setChannel] = useState<OtpChannel | null>(handover.channel ?? null);
  const [notice, setNotice] = useState<CodeNotice | null>(null);
  /** No code is live any more — three wrong tries — so a new one may be asked for now. */
  const [spent, setSpent] = useState(false);
  const [telegram, setTelegram] = useState<TelegramStep>({ kind: 'idle' });
  const [shown, setShown] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const cells = useRef<Array<HTMLInputElement | null>>([null, null, null, null]);
  /** In-flight guard for every request this screen makes. */
  const inFlight = useRef(false);
  const challenge = useRef<HTMLDivElement>(null);

  const left = useCountdown(expiresAt);
  const frame =
    shown === null
      ? null
      : renderAuthFailure(shown, {
          onRetry: () => setShown(null),
          onBack: () => setShown(null),
          onContinue: () => navigate('/onboarding/profile'),
        });
  const turnstile = useTurnstile(challenge, frame === null);

  const run = async (where: string, work: () => Promise<void>): Promise<void> => {
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    setNotice(null);
    try {
      await work();
    } catch (cause) {
      const answer = answerFor(noteFailure(where, cause));
      if (isInline(answer)) {
        setNotice({ kind: 'answer', answer });
        if (answer.kind === 'wrong_code') {
          // The code is spent once the tries are; the cells start again either way.
          if (answer.attempts_left === 0) setSpent(true);
          setDigits(EMPTY);
          cells.current[0]?.focus();
        }
      } else {
        setShown(cause);
      }
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };

  const verify = (code: string): void => {
    void run(WHERE_VERIFY, async () => {
      await verifyOtp({ phone_e164: handover.phone_e164, code });
      // The session is live from here: `verifyOtp` handed the access token to the
      // transport and wrote the refresh token down before it resolved.
      navigate(landingFor(handover), { replace: true });
    });
  };

  /** Ask for a new code on `via`. The same request A3 makes, with a fresh challenge token. */
  const resend = (via: OtpChannel | null): void => {
    void run(WHERE_RESEND, async () => {
      const result = await requestOtp({
        phone_e164: handover.phone_e164,
        challengeToken: takeTurnstileToken(),
        ...(via === null ? {} : { channel: via }),
      });
      if (result.outcome === 'deferred') {
        setShown(new Admitted(result.notice));
        return;
      }
      setExpiresAt(result.expires_at);
      setChannel(via);
      setSpent(false);
      setDigits(EMPTY);
      // Kept, not cleared — see `stepAfterResend`: the chat is already bound, and asking
      // `start-link` again would spend one of the five first-contact links an hour on a
      // step the person has done.
      setTelegram(stepAfterResend(telegram));
      setNotice({ kind: 'resent' });
      cells.current[0]?.focus();
    });
  };

  /** Door B's first half, from A4: ask for the link and open it. */
  const openTelegram = (): void => {
    void run(WHERE_RESEND, async () => {
      const result = await beginTelegram(handover.phone_e164);
      if (result.outcome === 'opened') {
        setTelegram({
          kind: 'opened',
          phone_e164: handover.phone_e164,
          deep_link: result.deep_link,
          expires_at_ms: Date.now() + result.expires_in_seconds * 1000,
        });
      } else if (result.outcome === 'throttled') {
        setNotice({ kind: 'answer', answer: { kind: 'throttled', retry_after_seconds: result.retry_after_seconds } });
      } else {
        setNotice({ kind: 'telegram_unavailable' });
      }
    });
  };

  /** The Telegram block: open the link first, then ask for the code over Telegram. */
  const telegramPressed = (): void => {
    if (telegram.kind === 'opened') resend('telegram');
    else openTelegram();
  };

  const complete = (next: readonly string[]): void => {
    const code = toAsciiDigits(next.join(''));
    if (next.every((d) => d !== '') && /^\d{4}$/.test(code)) verify(code);
  };

  const setDigit = (i: number, raw: string): void => {
    // One character, and only a digit in either script — shown in Bangla digits, the
    // script the frame draws its cell in (and the one Anek Bangla is set for here).
    const digit = toAsciiDigits(raw.slice(-1));
    const value = /^\d$/.test(digit) ? toBanglaDigits(digit) : '';
    const next = digits.map((d, j) => (j === i ? value : d));
    setDigits(next);
    if (value !== '' && i < 3) cells.current[i + 1]?.focus();
    complete(next);
  };

  /** Somebody pasted or typed the whole code into one cell. */
  const spread = (i: number, raw: string): boolean => {
    const ascii = toAsciiDigits(raw).replace(/\D/g, '');
    if (ascii.length < 2) return false;
    const next = digits.map((d, j) => (j >= i ? toBanglaDigits(ascii[j - i] ?? '') : d));
    setDigits(next);
    complete(next);
    return true;
  };

  if (frame !== null) return frame;

  const canResend = spent || left === 0;
  const copy = ONBOARDING_COPY.code;

  return (
    <Frame className={`${SAND_FRAME} px-[24px] pt-[20px] pb-[24px] overflow-y-auto`}>
      {/* `flex` — see phone.tsx: a block wrapper gives the inline-flex button a line
          box and shifts the whole column 10px down from the drawn frame. */}
      <div className="flex">
        <IconButton control="back" size={22} onClick={() => navigate(-1)} />
      </div>

      <div className="mt-[30px]">
        <h1 id="a4-title" className="m-0 font-anek font-semibold text-[24px] leading-[1.25] text-ink">
          কোডটি লিখুন
        </h1>
        {/* The drawn sentence, with the number the code was actually sent to in the
            place the design already drew a number. */}
        <div className="font-anek font-normal text-[15px] leading-[1.75] text-body mt-[7px]">
          {`${nationalBangla(handover.phone_e164)} নম্বরে চার সংখ্যার কোড পাঠানো হয়েছে।`}
        </div>
      </div>

      <div role="group" aria-labelledby="a4-title" className="flex gap-[10px] mt-[26px]">
        {CELLS.map((i) => {
          const value = digits[i] ?? '';
          const filled = value !== '';
          return (
            <input
              key={i}
              ref={(el) => {
                cells.current[i] = el;
              }}
              value={value}
              maxLength={1}
              inputMode="numeric"
              autoFocus={i === 0}
              autoComplete={i === 0 ? 'one-time-code' : 'off'}
              onFocus={(e) => e.currentTarget.select()}
              onKeyDown={(e) => {
                if (e.key === 'Backspace' && value === '' && i > 0) cells.current[i - 1]?.focus();
              }}
              onPaste={(e) => {
                if (spread(i, e.clipboardData.getData('text'))) e.preventDefault();
              }}
              onChange={(e) => {
                if (!spread(i, e.target.value)) setDigit(i, e.target.value);
              }}
              className={
                'flex-1 min-w-0 bg-white h-[64px] text-center outline-none rounded-none ' +
                'font-anek font-semibold text-[26px] leading-[1.25] text-ink ' +
                (filled ? 'border-[1.5px] border-teal' : 'border border-border')
              }
            />
          );
        })}
      </div>

      <div className="flex justify-between items-center mt-[17px]">
        {canResend ? (
          <button
            type="button"
            data-copy-status={ONBOARDING_COPY.copy_status}
            disabled={busy || !turnstile.ready}
            onClick={() => resend(channel)}
            className="font-anek font-medium text-[14px] leading-[1.75] text-link bg-transparent border-0 p-0 rounded-none disabled:text-muted"
          >
            {copy.resend_bn}
          </button>
        ) : (
          <div role="timer" aria-live="off" className="font-anek font-normal text-[14px] leading-[1.75] text-muted">
            {resendInBn(clockBn(left))}
          </div>
        )}
        <button
          type="button"
          onClick={() => navigate(handover.intent === 'login' ? '/auth/login' : '/auth/phone')}
          className="font-anek font-medium text-[14px] leading-[1.75] text-link bg-transparent border-0 p-0 rounded-none"
        >
          নম্বর বদলান
        </button>
      </div>

      {notice === null ? null : notice.kind === 'answer' ? (
        <InlineAnswer answer={notice.answer} unexplainedBn={copy.not_checked_bn} className="mt-[12px]" />
      ) : (
        <div
          role={notice.kind === 'resent' ? 'status' : 'alert'}
          data-copy-status={ONBOARDING_COPY.copy_status}
          className={
            'font-anek font-medium text-[13px] leading-[1.75] mt-[12px] ' +
            (notice.kind === 'resent' ? 'text-success' : 'text-danger-ink')
          }
        >
          {notice.kind === 'resent' ? copy.resent_bn : ONBOARDING_COPY.door.telegram_unavailable_bn}
        </div>
      )}

      <div className="flex-1 min-h-[16px]" />

      <div ref={challenge} className="empty:hidden flex" />

      <TelegramPanel
        step={telegram}
        onReopen={openTelegram}
        className="mb-[12px]"
      />

      {/* The drawn block — `background:#F4F0E7; border:1px solid #E2DED5; padding:14px 15px`
          — carrying the channel that exists instead of the one that does not. */}
      <div data-copy-status={ONBOARDING_COPY.copy_status} className="bg-sand border border-border px-[15px] py-[14px]">
        <div className="font-anek font-medium text-[15px] leading-[1.75] text-ink">{copy.help_title_bn}</div>
        <div className="font-anek font-normal text-[14px] leading-[1.75] text-body mt-[4px]">{copy.help_body_bn}</div>
        <button
          type="button"
          data-a4-telegram={telegram.kind}
          disabled={busy || (telegram.kind === 'opened' && !turnstile.ready)}
          onClick={telegramPressed}
          className="w-full border border-teal h-[46px] flex items-center justify-center font-anek font-semibold text-[15px] leading-[1.75] text-teal mt-[11px] bg-transparent rounded-none disabled:border-border disabled:text-muted"
        >
          {telegram.kind === 'opened' ? 'কোড পাঠান' : copy.telegram_bn}
        </button>
      </div>
    </Frame>
  );
}
